import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { format } from 'date-fns';
import { useReactToPrint } from 'react-to-print';
import { toast } from 'react-toastify';
import { PaymentLineItemType } from '@prisma/client';
import {
  FooterAction,
  LoadedUser,
  Section,
  SectionRow,
  Transaction,
} from '../../interfaces';
import SideBarModal from '../modal/SideBarModal';
import {
  formatDollarAmount,
  isUserAdminOrManager,
  getEmployeeNameFromAppointment,
} from '../../utils/global/helper';
import {
  checkPaymentType,
  convertPaymentsToTransactions,
  getPaymentLineItems,
  getProcessedByName,
  getTransactionCurrency,
  isCashDiscountEnabled,
} from '../../helpers/payments';
import TransactionRefundModal from './TransactionRefundModal';
import useUser from '../../hooks/global/useUser';
import useStateCooldown from '../../hooks/useStateCooldown';
import useVoidPayment from '../../hooks/payments/useVoidPayment';
import TransactionBadge from './TransactionBadge';
import PaymentLineItem from '../modal/PaymentLineItem';
import useGetPayment from '../../hooks/payments/useGetPayment';
import { getClientSection } from '../../utils/modal/paymentModal';
import { FullPayment } from '../../types/payments';
import './print.css';
import {
  ALLOWED_VOID_PAYMENT_METHODS,
  ALLOWED_VOID_PAYMENT_TYPES,
  CREDIT_PAYMENT_METHODS,
} from '../../constants/payments';
import useRefreshPayment from '../../hooks/payments/useRefreshPayment';
import useConfirm from '../../hooks/useConfirm';
import useNavigateToCustomerProfile from '../../hooks/customers/useNavigateToCustomerProfile';
import useAwaitedForm from '../../hooks/useAwaitedForm';
import { getAllUserServices } from '../../helpers/services';
import { getUserNameFromBusiness } from '../../helpers/global';
import GuestReceiptModal from '../form/GuestReceiptModal';

interface Props {
  open: boolean;
  onClose: () => void;
  transaction: Transaction | null;
}

const APPOINTMENT_LINE_ITEM_TYPES: PaymentLineItemType[] = [
  'SERVICE',
  'SERVICE_TAX',
];

function TransactionDetailRow({ transaction }: { transaction: Transaction }) {
  return (
    <Box>
      <Box
        key={`${transaction.id}-overview`}
        display="flex"
        alignItems="center">
        <Typography variant="body2">
          <Typography variant="h5" component="span">
            {formatDollarAmount(transaction.amount)}{' '}
          </Typography>
          {getTransactionCurrency(transaction)}{' '}
        </Typography>
        <TransactionBadge
          transaction={transaction}
          light={false}
          tooltipOffset={[0, -5]}
          sx={{
            ml: 1,
          }}
        />
      </Box>
      {(transaction.status === 'SUCCESS' || transaction.status === 'PENDING') &&
        transaction.error && (
          <Typography mt={0.25} variant="body2">
            {transaction.error}
          </Typography>
        )}
    </Box>
  );
}

function TransactionDetailModal({
  open,
  onClose,
  transaction: passedTransaction,
}: Props) {
  const confirm = useConfirm();
  const navigateToCustomerProfile = useNavigateToCustomerProfile();
  const asyncForm = useAwaitedForm();
  const { user } = useUser<LoadedUser>();

  const { data: payment, refetch } = useGetPayment(
    passedTransaction?.payment?.id,
  );

  const [isRefundActive, setIsRefundActive] = React.useState(false);
  const [isSendReceiptOpen, setIsSendReceiptOpen] = React.useState(false);
  const [disableSendReceipt, setDisableSendReceipt] = useStateCooldown(30_000);

  const businessCashDiscountEnabled = isCashDiscountEnabled(user.business);

  const transaction = React.useMemo(
    () =>
      payment
        ? convertPaymentsToTransactions([payment], {
            businessCashDiscountEnabled,
          })[0]
        : null,
    [payment, businessCashDiscountEnabled],
  );

  const onRefresh = async () => {
    await refetch();
  };

  const { mutateAsync: refreshPayment } = useRefreshPayment(onRefresh);
  const { mutate: voidPayment } = useVoidPayment({
    onSuccess: async p => {
      await refreshPayment(p);
      toast.success('Payment voided');
    },
    onError: err => {
      toast.error(err);
    },
  });

  const contentRef = React.useRef<HTMLDivElement>(null);
  const reactToPrintFn = useReactToPrint({ contentRef });

  const lastUpdated = transaction
    ? new Date(transaction.dateUpdated || transaction.dateCreated)
    : null;

  const processedByName = transaction
    ? getProcessedByName(user, transaction)
    : '';

  const { appointment, order, customer } = payment ?? {};

  const providerName = appointment
    ? getEmployeeNameFromAppointment(appointment)
    : getUserNameFromBusiness(user, false, order?.employeeId);

  const sections = !transaction
    ? []
    : ([
        {
          name: 'Overview',
          rows: [
            {
              type: 'NODE',
              label: 'Transaction',
              value: [<TransactionDetailRow transaction={transaction} />],
            },
            payment?.processedByUserId
              ? {
                  type: 'TEXT',
                  label: 'Processed By',
                  value: [processedByName],
                }
              : null,
            providerName
              ? {
                  type: 'TEXT',
                  label: 'Service Provider',
                  value: [providerName],
                }
              : null,
            {
              type: 'TEXT',
              label: 'Last Updated',
              value: [
                `${format(lastUpdated!, 'MMM d, yyyy')} at ${format(
                  lastUpdated!,
                  'h:mm a',
                )}`,
              ],
            },
            payment?.receiptEmail
              ? {
                  type: 'TEXT',
                  label: 'Receipt',
                  value: [payment.receiptEmail],
                }
              : null,
          ].filter(Boolean) as SectionRow[],
        },
        {
          name: 'Details',
          rows: [
            {
              type: 'NODE',
              label: '',
              value: [
                <Stack spacing={1} width="100%">
                  {getPaymentLineItems({
                    appointment: appointment ?? null,
                    order: order ?? null,
                    payments: [payment].filter(Boolean) as FullPayment[],
                    businessServices: getAllUserServices(user),
                    businessCashDiscountEnabled,
                  }).map(({ label, amount, isTotal }) => (
                    <PaymentLineItem
                      key={label}
                      label={label}
                      value={Math.abs(amount)}
                      isTotal={isTotal}
                      subtract={amount < 0 || isTotal}
                    />
                  ))}
                </Stack>,
              ],
            },
          ],
        },
        !!payment?.method && CREDIT_PAYMENT_METHODS.includes(payment.method)
          ? {
              name: 'Transfer Details',
              values: {
                payment,
              },
              rows: [],
            }
          : null,
        customer
          ? getClientSection(
              { customer: { ...customer, emergencyContacts: [] } },
              {
                text: 'View',
                action: () => navigateToCustomerProfile(customer.customerId),
              },
            )
          : null,
      ].filter(Boolean) as Section[]);

  const hasPaymentAccess =
    (!order ||
      isUserAdminOrManager(user) ||
      user.userId === order.employeeId) &&
    // only appointment.employeeId or managers can refund appointments
    (!appointment ||
      appointment.employeeId === user?.userId ||
      isUserAdminOrManager(user));
  const allowRefunds =
    user.canIssueRefunds &&
    !checkPaymentType(payment, 'IMPORT') &&
    payment?.status === 'SUCCESS' &&
    // this will only allow related employees to refund
    payment?.transfers?.length &&
    hasPaymentAccess;

  const footerActions: FooterAction[] = [
    allowRefunds
      ? {
          action: () => setIsRefundActive(true),
          label: 'Refund',
        }
      : null,
  ].filter(Boolean) as FooterAction[];

  const allowSendReceipt = payment?.status === 'SUCCESS';
  const allowVoid =
    !!payment &&
    checkPaymentType(payment, ALLOWED_VOID_PAYMENT_TYPES) &&
    ALLOWED_VOID_PAYMENT_METHODS.includes(payment?.method) &&
    payment?.status === 'SUCCESS' &&
    hasPaymentAccess;

  const moreActions = [
    allowSendReceipt && {
      action: () => {
        if (!transaction) return;
        if (disableSendReceipt) {
          toast.error('Please wait before sending another receipt');
          return;
        }
        setIsSendReceiptOpen(true);
      },
      label: 'Send receipt',
      disabled: !allowSendReceipt,
    },
    allowSendReceipt && {
      action: () => {
        reactToPrintFn();
      },
      label: 'Print details',
      disabled: !allowSendReceipt,
    },
    allowVoid && {
      action: async () => {
        const isAppointmentPayment = payment?.lineItems.some(li =>
          APPOINTMENT_LINE_ITEM_TYPES.includes(li.type),
        );
        const isSplitAppointmentPayment =
          (payment?.splitPaymentsGroup?.payments ?? []).filter(
            p =>
              p.id !== payment?.id &&
              p.lineItems.some(li =>
                APPOINTMENT_LINE_ITEM_TYPES.includes(li.type),
              ),
          ).length > 0;
        let behavior: 'open' | 'free' | undefined;

        if (
          isAppointmentPayment &&
          !isSplitAppointmentPayment &&
          appointment &&
          checkPaymentType(payment, 'FULL')
        ) {
          const formResponse = await asyncForm({
            form: {
              title: 'Void Transaction',
              initialValues: {
                behavior: 'open' as 'open' | 'free',
              },
              inputRows: [
                [
                  {
                    inputType: 'select',
                    label:
                      'Do you want to re-open the checkout for this appointment, or mark the appointment as completed for $0?',
                    isLabelMultiline: true,
                    autoWidth: false,
                    name: 'behavior',
                    options: [
                      {
                        label: 'Re-open checkout',
                        value: 'open',
                      },
                      {
                        label: 'Mark as completed for $0',
                        value: 'free',
                      },
                    ],
                  },
                ],
              ],
              submitButtonText: 'Void',
            },
            allowUntouched: true,
          });
          if (!formResponse) return;
          behavior = formResponse.behavior;
        } else {
          const confirmed = await confirm({
            title: 'Void Transaction',
            message:
              'Are you sure you want to void this transaction? This action cannot be undone.',
          });
          if (!confirmed) return;
        }

        voidPayment({
          paymentId: payment?.id,
          appointmentPaymentBehavior: behavior,
        });
      },
      label: 'Void transaction',
      disabled: !allowVoid,
    },
  ].filter(Boolean) as FooterAction[];

  return (
    <>
      <SideBarModal
        modalOpen={open}
        name="Transaction Details"
        sections={sections}
        footerActions={footerActions}
        moreActions={moreActions}
        setModalOpen={onClose}
        isLoading={!transaction}
        ref={contentRef}
      />
      {!!transaction && (
        <TransactionRefundModal
          transaction={transaction}
          open={isRefundActive}
          onClose={() => setIsRefundActive(false)}
          onRefresh={onRefresh}
        />
      )}
      <GuestReceiptModal
        open={isSendReceiptOpen}
        paymentId={transaction?.id ?? null}
        initialEmail={customer?.user?.email ?? null}
        initialPhone={customer?.user?.phoneNumber ?? null}
        showNoReceipt={false}
        onClose={() => setIsSendReceiptOpen(false)}
        onSent={() => setDisableSendReceipt(true)}
      />
    </>
  );
}

export default TransactionDetailModal;
